import { useState, useEffect } from 'react'
import { Spinner } from '@heroui/react'
import { useAuthStore } from '../../../store/useAuthStore'
import { checkSessionExpired } from '../../../api/client'
import { ChartViewInner, ChartStatic } from '../ChartView'
import { ChartLoginPlaceholder, useChartHidden } from './ChartAuthGate'

// Jednotný fetcher grafu — VŽDY stahuje format=json[<fmt>,meta] (i "neinteraktivní"
// režim), aby byla k dispozici meta.toFill (řady k dotažení). Nahrazuje přímé
// použití ChartImage/ChartView v graf-view.
//
// props:
//   url          — kompletní URL grafu (volající si už doplní &format=json[<fmt>,meta])
//   gran         — '10m' | 'hly' | 'dly' (pro crosshair přepočet času)
//   interactive  — true → crosshair vrstva (ChartViewInner), false → jen obrázek (ChartStatic)
//   onMeta       — callback(meta|null) po každém (ne)úspěšném fetchi; kvůli meta.toFill
//   deriveTooltip — volitelný callback pro přepis řádků tooltipu (viz ChartViewInner)
export function ChartFrame({ url, gran, interactive, onMeta, deriveTooltip }) {
  const [data, setData] = useState(null)
  const [state, setState] = useState('loading') // loading | ok | error
  const token = useAuthStore(s => s.token)
  const chartHidden = useChartHidden()

  useEffect(() => {
    let cancelled = false
    if (chartHidden) return   // anonym → žádný fetch, jen placeholder
    setState('loading')
    setData(null)

    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    // Cache řídí server hlavičkou Cache-Control podle meta.toFill: kompletní graf
    // private, max-age=300; graf s dírami no-store. Viz
    // cw-server/doc/plans/chart-meta-to-fill.md, Část B2.
    fetch(url, { headers })
      .then(r => { if (!r.ok) { checkSessionExpired(r); throw new Error(`HTTP ${r.status}`) }; return r.json() })
      .then(d => {
        if (cancelled) return
        setData(d)
        setState('ok')
        onMeta?.(d?.meta ?? null)
      })
      .catch(e => {
        if (cancelled) return
        console.error('[ChartFrame] Chyba načítání grafu:', url, e)
        setState('error')
        onMeta?.(null)
      })

    return () => { cancelled = true }
    // onMeta je stabilní (setState z volajícího) — záměrně mimo deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token, chartHidden])

  if (chartHidden) return <ChartLoginPlaceholder />
  if (state === 'loading') return (
    <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
      <Spinner size="sm" /> Načítání grafu…
    </div>
  )
  if (state === 'error') return <p className="text-red-500 text-sm py-4">Chyba při načítání grafu</p>

  return interactive
    ? <ChartViewInner data={data} gran={gran} deriveTooltip={deriveTooltip} />
    : <ChartStatic data={data} />
}

// Přidá &format=json[<fmt>,meta] do URL grafu (fmt = png | jpg | svg).
export function withJsonFormat(url, fmt) {
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}format=${encodeURIComponent(`json[${fmt},meta]`)}`
}
